/* eslint-disable no-console */
import React, { Component } from 'react';
import ContentLoader from 'react-content-loader';
// our components
import RouterContext from 'helpers/routerContext';
import CreditBundleCreate from 'components/CreditBundle/Create/Create';
import { adopt } from 'react-adopt';
import { Mutation, Query } from 'components/common/Query';
import gql from 'graphql-tag';
import { CREDIT_BUNDLE } from 'routes/paths';
import { withApollo } from 'react-apollo';

/**
 * Query to fetch an Ad' user from api
 */
export const AD_USER_QUERY = gql`
  query AdUser($id: Int!) {
    adUser(id: $id) {
      id
      publicId
      fakeId
      fullname
    }
  }
`;

/**
 * Mutation to create a credit bundle
 */
export const CREATE_CREDIT_BUNDLE_MUTATION = gql`
  mutation CreateCreditBundle(
    $ad: ID
    $user: ID
    $agency: ID!
    $currency: AdminCreditBundleFormCurrency!
    $secondaryCurrency: AdminCreditBundleFormSecondaryCurrency
    $price: Float!
  ) {
    createCreditBundle(
      input: {
        ad: $ad
        user: $user
        agency: $agency
        currency: $currency
        secondaryCurrency: $secondaryCurrency
        price: $price
      }
    ) {
      errors {
        field
        messages
      }
      clientMutationId
    }
  }
`;

/**
 * Query to fetch a credit bundle prices
 */
export const CREDIT_BUNDLE_PRICES_QUERY = gql`
  query CreditBundlePrices {
    creditBundlePrices {
      currency
      price
      basePrice
    }
  }
`;

class CreditBundleCreateContainer extends Component {
  static contextType = RouterContext;

  constructor(props) {
    super(props);
    this.state = {
      errors: [],
      adUser: null,
    };
    this.Compose = adopt({
      mutate: this.CreateCreditBundle,
      queryCreditBundlePrices: this.CreditBundlePrices,
    });
  }

  /**
   * @description
   * Mutation create credit bundle callback.
   *
   * @param {*} render - Mutation's render props.
   * @returns {*} Mutation component.
   */
  CreateCreditBundle = ({ render }) => (
    <Mutation
      mutation={CREATE_CREDIT_BUNDLE_MUTATION}
      onCompleted={this.CreateCreditBundleCompleted}
    >
      {(handle, { loading }) =>
        render({
          handle,
          loading,
        })
      }
    </Mutation>
  );

  /**
   * @description
   * Mutation callback when create a credit bundle was completed.
   *
   * @param {*} errors - Mutation's onCompleted props.
   * @returns {*} No returns value.
   */
  CreateCreditBundleCompleted = ({ createCreditBundle: { errors } }) => {
    const history = this.context;
    if (!errors) {
      history.push({
        pathname: CREDIT_BUNDLE.LIST,
      });
    } else {
      this.setState({ errors });
    }
  };

  /**
   * @description
   * CreditBundlePrices query callback to compose with mutation.
   *
   * @param {*} render - Query's render props.
   * @returns {*} Query component.
   */
  CreditBundlePrices = ({ render }) => (
    <Query
      query={CREDIT_BUNDLE_PRICES_QUERY}
      variables={{}}
      notifyOnNetworkStatusChange
      fetchPolicy="network-only"
    >
      {({ data, loading }) =>
        render({
          creditBundlePricesData: data,
          loadingCreditBundlePrices: loading,
        })
      }
    </Query>
  );

  handleGetAd = async (ad, callback) => {
    const { client } = this.props;

    if (!ad) return;

    const { errors, data } = await client.query({
      query: AD_USER_QUERY,
      variables: { id: ad },
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    });
    if (errors) {
      this.setState({
        adUser: null,
        errors: [{ field: 'ad', messages: errors[0].message }],
      });
    } else {
      this.setState({
        adUser: data.adUser,
        errors: [],
      });
      if (callback) {
        callback(data.adUser);
      }
    }
  };

  /**
   * @description
   * Render CreditBundleForm component.
   *
   * @returns {*} Component to render.
   */
  render() {
    const { errors, adUser } = this.state;

    return (
      <this.Compose>
        {({
          mutate: { handle, loading },
          queryCreditBundlePrices: {
            creditBundlePricesData,
            loadingCreditBundlePrices,
          },
        }) => {
          if (!loading && !loadingCreditBundlePrices) {
            return (
              <CreditBundleCreate
                onSave={values => handle({ variables: values })}
                submitLoading={loading}
                errors={errors}
                closeNotification={() => this.setState({ errors: [] })}
                creditBundlePrices={creditBundlePricesData.creditBundlePrices}
                onResetAd={() => this.setState({ adUser: null })}
                onGetAd={this.handleGetAd}
                adUser={adUser}
              />
            );
          }
          return (
            <ContentLoader
              height={938}
              width={634}
              speed={2}
              primaryColor="#d9d9d9"
              secondaryColor="#ecebeb"
            >
              <path
                // eslint-disable-next-line max-len
                d="M23,773 L71.7969104,773 C84.4994597,773 94.7969104,783.297451 94.7969104,796 C94.7969104,808.702549 84.4994597,819 71.7969104,819 L23,819 C10.2974508,819 5.10832731e-15,808.702549 3.55271368e-15,796 C1.99710005e-15,783.297451 10.2974508,773 23,773 Z M440,773 L611.192921,773 C623.895471,773 634.192921,783.297451 634.192921,796 C634.192921,808.702549 623.895471,819 611.192921,819 L440,819 C427.297451,819 417,808.702549 417,796 C417,783.297451 427.297451,773 440,773 Z M513,683 L610.995307,683 C623.697856,683 633.995307,693.297451 633.995307,706 C633.995307,718.702549 623.697856,729 610.995307,729 L513,729 C500.297451,729 490,718.702549 490,706 C490,693.297451 500.297451,683 513,683 Z M340,683 L437.995307,683 C450.697856,683 460.995307,693.297451 460.995307,706 C460.995307,718.702549 450.697856,729 437.995307,729 L340,729 C327.297451,729 317,718.702549 317,706 C317,693.297451 327.297451,683 340,683 Z M8,94 L626,94 C630.418278,94 634,97.581722 634,102 L634,123 C634,127.418278 630.418278,131 626,131 L8,131 C3.581722,131 5.41083001e-16,127.418278 0,123 L0,102 C-5.41083001e-16,97.581722 3.581722,94 8,94 Z M8,451 L174.394056,451 C178.812334,451 182.394056,454.581722 182.394056,459 L182.394056,461 C182.394056,465.418278 178.812334,469 174.394056,469 L8,469 C3.581722,469 5.41083001e-16,465.418278 0,461 L0,459 C-5.41083001e-16,454.581722 3.581722,451 8,451 Z M8,697 L174.394056,697 C178.812334,697 182.394056,700.581722 182.394056,705 L182.394056,707 C182.394056,711.418278 178.812334,715 174.394056,715 L8,715 C3.581722,715 5.41083001e-16,711.418278 0,707 L0,705 C-5.41083001e-16,700.581722 3.581722,697 8,697 Z M8,156 L185.193704,156 C189.611982,156 193.193704,159.581722 193.193704,164 L193.193704,185 C193.193704,189.418278 189.611982,193 185.193704,193 L8,193 C3.581722,193 5.41083001e-16,189.418278 0,185 L0,164 C-5.41083001e-16,159.581722 3.581722,156 8,156 Z M8,479 L626,479 C630.418278,479 634,482.581722 634,487 L634,650 C634,654.418278 630.418278,658 626,658 L8,658 C3.581722,658 5.41083001e-16,654.418278 0,650 L0,487 C-5.41083001e-16,482.581722 3.581722,479 8,479 Z M8,303 L116.795933,303 C121.214211,303 124.795933,306.581722 124.795933,311 L124.795933,399 C124.795933,403.418278 121.214211,407 116.795933,407 L8,407 C3.581722,407 5.41083001e-16,403.418278 0,399 L-8.8817842e-16,311 C-1.42926142e-15,306.581722 3.581722,303 8,303 Z M191,303 L299.795933,303 C304.214211,303 307.795933,306.581722 307.795933,311 L307.795933,399 C307.795933,403.418278 304.214211,407 299.795933,407 L191,407 C186.581722,407 183,403.418278 183,399 L183,311 C183,306.581722 186.581722,303 191,303 Z M373,303 L481.795933,303 C486.214211,303 489.795933,306.581722 489.795933,311 L489.795933,399 C489.795933,403.418278 486.214211,407 481.795933,407 L373,407 C368.581722,407 365,403.418278 365,399 L365,311 C365,306.581722 368.581722,303 373,303 Z M8,220 L401,220 C405.418278,220 409,223.581722 409,228 L409,258 C409,262.418278 405.418278,266 401,266 L8,266 C3.581722,266 5.41083001e-16,262.418278 0,258 L0,228 C-5.41083001e-16,223.581722 3.581722,220 8,220 Z M7,0 L207,0 C210.865993,-7.10171439e-16 214,3.13400675 214,7 L214,32 C214,35.8659932 210.865993,39 207,39 L7,39 C3.13400675,39 4.73447626e-16,35.8659932 0,32 L-8.8817842e-16,7 C-1.36162605e-15,3.13400675 3.13400675,2.48652828e-15 7,1.77635684e-15 Z"
                fill="#E5EAEC"
              />
            </ContentLoader>
          );
        }}
      </this.Compose>
    );
  }
}

export default withApollo(CreditBundleCreateContainer);
